import { Link as GatsbyLink } from "gatsby"

import { Link as MUILink, type LinkProps as MUILinkProps } from "@mui/material"

import { useLink } from "@ggs/hooks"
import type { FC } from ".."

export interface LinkProps extends MUILinkProps {
  type?: "base" | "overlay" | "underline" | "underline-reverse"
  link?: { title: string; uri: string }
  href?: string
  isInternal?: boolean
}

const Link: FC<LinkProps> = ({ type = "underline", link, href, ...props }) => {
  const { isInternal, isAssetLink, isExternal, isJumpLink, uri } = useLink({ link, href })

  const commonProps = {
    ...(uri && {
      component: isInternal ? GatsbyLink : "a",
      ...(isAssetLink || isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {}),
      ...(isAssetLink ? { download: true } : {}),
      ...(isAssetLink || isExternal || isJumpLink ? { href: uri } : { to: uri }),
    }),
  }

  switch (type) {
    case "base":
      return <LinkBase {...commonProps} {...props} />
    case "overlay":
      return <LinkOverlay {...commonProps} {...props} />
    case "underline-reverse":
      return <LinkUnderlineReverse {...commonProps} {...props} />
    case "underline":
    default:
      return <LinkUnderline {...commonProps} {...props} />
  }
}
export default Link

export const LinkOverlay: FC<LinkProps> = ({ sx, ...props }) => {
  return (
    <MUILink
      underline="none"
      sx={[
        {
          display: "block",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
        },
        ...(Array.isArray(sx) ? sx : [sx || {}]),
      ]}
      {...props}
    />
  )
}

const LinkUnderline: FC<LinkProps> = ({ sx, ...props }) => (
  <MUILink
    variant="link.md"
    underline="none"
    sx={[
      {
        "color": (theme) => theme.palette["neutral.700"].main,
        "background": (theme) =>
          `linear-gradient(to right, ${theme.palette["primary.800"].main}, ${theme.palette["primary.800"].main})`,
        "backgroundSize": "100% 1px",
        "backgroundPosition": "0 100%",
        "backgroundRepeat": "no-repeat",
        "pb": 1.5,
        "transition": "background-size 400ms",

        "&:hover": {
          backgroundSize: "0 1px",
        },
      },
      ...(Array.isArray(sx) ? sx : [sx || {}]),
    ]}
    {...props}
  />
)

const LinkUnderlineReverse: FC<LinkProps> = ({ sx, ...props }) => (
  <MUILink
    variant="link.md"
    underline="none"
    sx={[
      {
        "color": (theme) => theme.palette["neutral.700"].main,
        "background": (theme) =>
          `linear-gradient(to right, ${theme.palette["primary.800"].main}, ${theme.palette["primary.800"].main})`,
        "backgroundSize": "0 1px",
        "backgroundPosition": "0 100%",
        "backgroundRepeat": "no-repeat",
        "pb": 1.5,
        "transition": "background-size 400ms",

        "&:hover": {
          backgroundSize: "100% 1px",
        },
      },
      ...(Array.isArray(sx) ? sx : [sx || {}]),
    ]}
    {...props}
  />
)

const LinkBase: FC<LinkProps> = ({ sx, ...props }) => (
  <MUILink
    variant="link.md"
    underline="none"
    sx={[
      { color: (theme) => theme.palette["neutral.700"].main },
      ...(Array.isArray(sx) ? sx : [sx || {}]),
    ]}
    {...props}
  />
)
